import React, { useEffect, useState } from 'react';
import * as firebaseui from 'firebaseui';
import { auth } from '../firebase/config';
import { GoogleAuthProvider, PhoneAuthProvider } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import 'firebaseui/dist/firebaseui.css';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Phone, Mail } from 'lucide-react';
import PhoneAuthModal from './PhoneAuthModal';
import EmailOTPModal from './EmailOTPModal';
import { Link } from 'react-router-dom';

// Detect if running in a mobile app webview
const isWebView = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  // Check for common webview indicators
  const isAndroidWebView = /wv/.test(ua) || /Android.*Version\/[0-9]/.test(ua) && !/Chrome\/[0-9]/.test(ua);
  const isIOSWebView = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(ua);
  const isWebViewUA = /\bwv\b/.test(ua) || /WebView/.test(ua);
  // Also check for standalone mode (PWA/WebView)
  const isStandalone = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
  // Check URL param for forcing webview mode (useful for testing)
  const urlParams = new URLSearchParams(window.location.search);
  const forceWebView = urlParams.get('webview') === 'true';
  
  return isAndroidWebView || isIOSWebView || isWebViewUA || forceWebView;
};

const SignInModal = ({ open, onOpenChange, redirectPath = null, noRedirect = false }) => {
  const { user } = useAuth();
  const [showPhoneAuth, setShowPhoneAuth] = useState(false);
  const [showEmailOTP, setShowEmailOTP] = useState(false);
  const [inWebView, setInWebView] = useState(false);

  useEffect(() => {
    setInWebView(isWebView());
  }, []);

  useEffect(() => {
    // Don't show Google SSO in webview
    if (!open || user || inWebView) {
      return;
    }

    // Wait for the modal to be fully rendered
    const timer = setTimeout(() => {
      const container = document.getElementById('firebaseui-auth-container');
      if (!container) return;

      // Initialize FirebaseUI
      const ui = firebaseui.auth.AuthUI.getInstance() || new firebaseui.auth.AuthUI(auth);
      
      const uiConfig = {
        callbacks: {
          signInSuccessWithAuthResult: (authResult, redirectUrl) => {
            console.log('✅ Firebase UI: Sign-in successful!', authResult.user.email);
            // Close modal after successful sign-in
            onOpenChange(false);
            
            // Redirect to specified path if provided and noRedirect is false
            if (redirectPath && !noRedirect) {
              setTimeout(() => {
                window.location.href = redirectPath;
              }, 100);
            }
            
            return false; // Prevent automatic redirect
          },
          signInFailure: (error) => {
            console.error('❌ Firebase UI: Sign-in failed:', error);
          }
        },
        signInFlow: 'popup',
        signInOptions: [
          {
            provider: GoogleAuthProvider.PROVIDER_ID,
            customParameters: {
              prompt: 'select_account'
            }
          }
        ],
        tosUrl: '/terms-and-conditions',
        privacyPolicyUrl: '/terms-and-conditions',
      };

      try {
        ui.start('#firebaseui-auth-container', uiConfig);
        
        // Hide Firebase UI's default terms text since we show our own
        setTimeout(() => {
          const tosElement = document.querySelector('.firebaseui-tos');
          if (tosElement) {
            tosElement.style.display = 'none';
          }
        }, 500);
      } catch (error) {
        console.error('Error starting Firebase UI:', error);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      // Reset Firebase UI when modal closes
      const ui = firebaseui.auth.AuthUI.getInstance();
      if (ui) {
        try {
          ui.reset();
        } catch (e) {
          // Ignore reset errors
        }
      }
    };
  }, [open, user, onOpenChange, redirectPath, inWebView]);

  // Close modal if user becomes authenticated
  useEffect(() => {
    if (user && open) {
      onOpenChange(false);
      // Only redirect if redirectPath is provided AND we're not on that path already
      if (redirectPath && !window.location.pathname.startsWith(redirectPath.split('?')[0])) {
        setTimeout(() => {
          window.location.href = redirectPath;
        }, 100);
      }
    }
  }, [user, open, onOpenChange, redirectPath]);

  return (
    <>
      <Dialog open={open && !showPhoneAuth && !showEmailOTP} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl font-bold bg-gradient-to-r from-amber-600 to-yellow-500 bg-clip-text text-transparent">
              Welcome to Piper Card
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-center text-gray-600 mb-6 text-sm">
              Sign in to create your digital business card
            </p>
            
            {/* Google SSO - Only show when NOT in webview */}
            {!inWebView && (
              <>
                <div id="firebaseui-auth-container"></div>
                
                {/* Terms notice for SSO */}
                <p className="text-center text-xs text-gray-500 mt-2">
                  By continuing, you are indicating that you accept our{' '}
                  <Link to="/terms-and-conditions" className="text-blue-500 hover:underline">
                    Terms & Conditions
                  </Link>
                </p>
                
                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-gray-500">Or continue with</span>
                  </div>
                </div>
              </>
            )}
            
            {/* Email OTP Auth Button */}
            <Button
              onClick={() => setShowEmailOTP(true)}
              className="w-full mb-3 bg-gradient-to-r from-amber-600 to-pink-500 hover:from-amber-700 hover:to-pink-600 text-white flex items-center justify-center gap-2"
            >
              <Mail className="w-5 h-5" />
              Sign in with Email OTP
            </Button>
            
            {/* Custom Phone Auth Button */}
            <Button
              onClick={() => setShowPhoneAuth(true)}
              className="w-full bg-green-600 hover:bg-green-700 text-white flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Sign in with Phone
            </Button>
            
            {/* Terms & Conditions - Always visible (for webview or as fallback) */}
            {inWebView && (
              <p className="text-center text-xs text-gray-500 mt-4">
                By continuing, you are indicating that you accept our{' '}
                <Link to="/terms-and-conditions" className="text-blue-500 hover:underline">
                  Terms & Conditions
                </Link>
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      <EmailOTPModal 
        open={showEmailOTP} 
        onClose={() => {
          setShowEmailOTP(false);
          onOpenChange(false);
        }}
        redirectPath={noRedirect ? null : redirectPath}
      />
      
      <PhoneAuthModal 
        open={showPhoneAuth} 
        onClose={() => {
          setShowPhoneAuth(false);
          onOpenChange(false);
        }}
        redirectPath={noRedirect ? null : redirectPath}
      />
    </>
  );
};

export default SignInModal;